import type {
  IClientDetailedDowntimeSummary,
  IClientDowntimeSummary,
  IDowntime,
  IGroupedDowntime,
  IInfrastructureDowntimeSummary,
  IMinerDowntimeDetail,
  IMinersDowntimeSummary,
} from '@/types/eos/DowntimeEvent'
import type {
  IClientDowntimeSummaryFilters,
  IDowntimeClientDetailSummaryFilters,
  IDowntimeFilters,
  IInfrastructureDowntimeSummaryFilters,
  IMinersDowntimeSummaryExportFilters,
  IMinersDowntimeSummaryFilters,
} from '@/types/eos/filters'
import type { IDowntimeCreationForm } from '@/types/eos/forms'
import type { IPage } from '@/types/shared/pagination'
import axios, { type AxiosRequestConfig } from 'axios'

export async function getGroupedDowntimes(filters: IDowntimeFilters): Promise<IPage<IGroupedDowntime>> {
  const res = await axios.get(`${import.meta.env.VITE_APP_EOSS_API}/downtimes`, {
    params: filters,
  })
  return res.data
}

export async function getDowntimeDetails(
  siteId: string,
  forHourUtc: string,
  downtimeTypeId: number
): Promise<IDowntime[]> {
  const res = await axios.get(`${import.meta.env.VITE_APP_EOSS_API}/downtimes/details`, {
    params: {
      site_id: siteId,
      for_hour_utc: forHourUtc,
      downtime_type_id: downtimeTypeId,
    },
  })
  return res.data
}

export async function bulkDeleteDowntimes(ids: number[]) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_EOSS_API}/downtimes`, {
    data: {
      downtime_ids: ids,
    },
  })
  return res.data
}

export async function getClientsDowntimeSummary(
  filters: IClientDowntimeSummaryFilters
): Promise<IClientDowntimeSummary[]> {
  const res = await axios.get(`${import.meta.env.VITE_APP_EOSS_API}/downtimes/client-summary`, {
    params: filters,
  })
  return res.data
}

export async function getClientDetailedDowntimeSummary(
  hostingClientId: string,
  filters: IDowntimeClientDetailSummaryFilters
): Promise<IClientDetailedDowntimeSummary[]> {
  const res = await axios.get(`${import.meta.env.VITE_APP_EOSS_API}/downtimes/client-summary/${hostingClientId}`, {
    params: filters,
  })
  return res.data
}

export async function getSitesDowntimeSummary(
  filters: IInfrastructureDowntimeSummaryFilters
): Promise<IInfrastructureDowntimeSummary[]> {
  const res = await axios.get(`${import.meta.env.VITE_APP_EOSS_API}/downtimes/infrastructure-summary/sites`, {
    params: filters,
  })
  return res.data
}

export async function getStructuresDowntimeSummary(
  filters: IInfrastructureDowntimeSummaryFilters
): Promise<IInfrastructureDowntimeSummary[]> {
  const res = await axios.get(`${import.meta.env.VITE_APP_EOSS_API}/downtimes/infrastructure-summary/structures`, {
    params: filters,
  })
  return res.data
}

export async function getRacksDowntimeSummary(
  filters: IInfrastructureDowntimeSummaryFilters
): Promise<IInfrastructureDowntimeSummary[]> {
  const res = await axios.get(`${import.meta.env.VITE_APP_EOSS_API}/downtimes/infrastructure-summary/racks`, {
    params: filters,
  })
  return res.data
}

export async function getMinersDowntimeSummary(
  filters: IMinersDowntimeSummaryFilters
): Promise<IPage<IMinersDowntimeSummary>> {
  const res = await axios.get(`${import.meta.env.VITE_APP_EOSS_API}/downtimes/infrastructure-summary/miners`, {
    params: filters,
  })
  return res.data
}

export async function exportMinersDowntimeSummary(filters: IMinersDowntimeSummaryExportFilters): Promise<Blob | void> {
  const config: AxiosRequestConfig = {
    params: filters,
  }

  if (filters.type === 'period') {
    config.responseType = 'blob'
  }

  const res = await axios.get(
    `${import.meta.env.VITE_APP_EOSS_API}/downtimes/infrastructure-summary/miners/export`,
    config
  )

  if (filters.type === 'period') {
    return res.data as Blob
  }
}

export async function createDowntime(form: IDowntimeCreationForm) {
  const res = await axios.post(`${import.meta.env.VITE_APP_EOSS_API}/downtimes`, form)
  return res.data
}

export async function removeMinersFromDowntime(downtimeID: number, minerIDs: string[]) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_EOSS_API}/downtimes/${downtimeID}/miners`, {
    data: {
      miner_ids: minerIDs,
    },
  })
  return res.data
}

export async function getMinerDowntimeDetails(
  minerID: string,
  filters: IInfrastructureDowntimeSummaryFilters
): Promise<IMinerDowntimeDetail[]> {
  const res = await axios.get(
    `${import.meta.env.VITE_APP_EOSS_API}/downtimes/infrastructure-summary/miners/${minerID}`,
    {
      params: filters,
    }
  )
  return res.data
}
